/**
 * Confrontatie-scène (§5.2): gezichtsloze baas-blob + monoloog → 3 houdingen →
 * per-chakra reactieregel → "Begin gevecht" (past de houding-bonus toe).
 */
import { useState } from 'react';
import type { Origin } from '../data/types';
import { CHAKRAS } from '../data/chakras';
import { HEADERS } from '../data/headers';
import { CONFRONTATIE_REACTIES, HOUDINGEN, type Houding } from '../data/vn';
import { baasMonoloog, baasTitel, confrontatieReactie } from '../data/content';
import { useProgressStore } from '../store/progressStore';
import { GidsTekst } from './GidsTekst';
import { ChakraAchtergrond } from './art/ChakraAchtergrond';
import { BaasBlob } from './art/BaasBlob';
import { t, type Taal } from '../i18n';

interface Props {
  chakra: Origin;
  baasIndex: number;
  taal: Taal;
  onStart: (houding: Houding) => void;
}

export function ConfrontatieScene({ chakra, baasIndex, taal, onStart }: Props) {
  const header = HEADERS[chakra];
  const baas = CHAKRAS[chakra].bazen[baasIndex];
  const logHouding = useProgressStore((s) => s.logHouding);
  const [monoloogKlaar, setMonoloogKlaar] = useState(false);
  const [gekozen, setGekozen] = useState<Houding | null>(null);

  const begin = () => {
    if (!gekozen) return;
    logHouding(chakra, baas.naam, gekozen, CONFRONTATIE_REACTIES[chakra][gekozen]);
    onStart(gekozen);
  };

  return (
    <ChakraAchtergrond chakra={chakra}>
    <div className="flex h-full w-full flex-col items-center justify-center gap-6 p-6 text-slate-100">
      <BaasBlob kleur={header.kleur} accent={header.accent} grootte={112} />
      <div className="text-center">
        <div className="text-xl font-bold" style={{ fontFamily: 'Cinzel, serif', color: '#ece6da' }}>{baas.naam}</div>
        <div className="text-sm text-slate-400">— {baasTitel(baas, taal)}</div>
      </div>

      <GidsTekst tekst={baasMonoloog(baas, taal)} kleur={header.accent} onKlaar={() => setMonoloogKlaar(true)} klaarLabel="…" />

      {monoloogKlaar && !gekozen && (
        <div className="flex flex-col items-center gap-3">
          <div className="text-sm uppercase tracking-widest text-slate-500">{t('vn.kiesHouding', taal)}</div>
          <div className="flex flex-wrap justify-center gap-3">
            {HOUDINGEN.map((h) => (
              <button
                key={h}
                type="button"
                onClick={() => setGekozen(h)}
                className="flex w-44 flex-col gap-1 rounded-xl border p-3 transition hover:-translate-y-1"
                style={{ borderColor: header.accent, background: `linear-gradient(160deg, #14121c, ${header.kleur}22)` }}
              >
                <span className="font-semibold" style={{ color: '#ece6da' }}>{t(`houding.${h}`, taal)}</span>
                <span className="text-[11px]" style={{ color: header.accent }}>{t(`houding.${h}.bonus`, taal)}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {gekozen && (
        <div className="flex flex-col items-center gap-4">
          <p className="max-w-xl text-center text-base italic text-slate-300" style={{ fontFamily: 'EB Garamond, serif' }}>
            “{confrontatieReactie(chakra, gekozen, CONFRONTATIE_REACTIES[chakra][gekozen], taal)}”
          </p>
          <button type="button" onClick={begin} className="rounded-lg px-6 py-2 font-semibold text-black transition hover:brightness-110" style={{ background: header.accent }}>
            {t('vn.beginGevecht', taal)} →
          </button>
        </div>
      )}
    </div>
    </ChakraAchtergrond>
  );
}
